<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useHrApi } from '../composables/useHrApi'
import { formatMinutesAsHours, formatNumber, monthValue } from '../utils/format'
import type { AttendanceReportRow, Option } from '../types/hr'

const { t } = useI18n()
const { attendanceReport, listWorkplaceOptions } = useHrApi()

const rows = ref<AttendanceReportRow[]>([])
const departments = ref<Option[]>([])
const month = ref(monthValue(new Date().toISOString().slice(0, 10)))
const departmentId = ref<string | number>('')
const loading = ref(true)
const error = ref('')

function rangeFor(value: string): { from: string; to: string } {
  const [year, monthPart] = value.split('-').map(Number)
  if (!year || !monthPart) return { from: '', to: '' }
  const lastDay = new Date(year, monthPart, 0).getDate()
  return {
    from: `${value}-01`,
    to: `${value}-${String(lastDay).padStart(2, '0')}`,
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const range = rangeFor(month.value)
    rows.value = await attendanceReport({
      date_from: range.from || undefined,
      date_to: range.to || undefined,
      department_agent_id: departmentId.value || undefined,
    })
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadDepartments() {
  try {
    departments.value = await listWorkplaceOptions()
  } catch {
    departments.value = []
  }
}

onMounted(() => {
  void loadDepartments()
  void load()
})

watch([month, departmentId], () => {
  void load()
})

const totals = computed(() => {
  const recorded = rows.value.reduce((sum, row) => sum + row.total_days, 0)
  const attended = rows.value.reduce((sum, row) => sum + row.attended_days, 0)
  const absent = rows.value.reduce((sum, row) => sum + row.absent_days, 0)
  const minutes = rows.value.reduce((sum, row) => sum + row.total_worked_minutes, 0)
  const rate = recorded === 0 ? 0 : Math.round((attended / recorded) * 100)
  return { recorded, attended, absent, minutes, rate, employees: rows.value.length }
})

function rateOf(row: AttendanceReportRow): string {
  if (!row.total_days) return '—'
  return `${Math.round((row.attended_days / row.total_days) * 100)}%`
}
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('hr.attendanceReport.title') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="month" class="date-input" type="month">
    <select v-model="departmentId" class="filter-select">
      <option value="">{{ t('hr.attendanceReport.allDepartments') }}</option>
      <option v-for="department in departments" :key="department.id" :value="department.id">{{ department.name }}</option>
    </select>
    <span class="spacer" />
    <button class="btn-secondary" type="button" @click="load">{{ t('hr.common.refresh') }}</button>
  </div>

  <div class="summary-row">
    <div class="summary-card">
      <span class="summary-value">{{ totals.employees }}</span>
      <span class="summary-label">{{ t('hr.attendanceReport.employeesCovered') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ totals.recorded }}</span>
      <span class="summary-label">{{ t('hr.attendanceReport.recorded') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ totals.rate }}%</span>
      <span class="summary-label">{{ t('hr.attendanceReport.averageRate') }}</span>
    </div>
    <div class="summary-card accent">
      <span class="summary-value">{{ formatNumber(totals.minutes / 60, 1) }}</span>
      <span class="summary-label">{{ t('hr.attendanceReport.workedHours') }}</span>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('hr.attendanceReport.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
  </div>

  <div v-else-if="rows.length === 0" class="state-message"><p>{{ t('hr.attendanceReport.empty') }}</p></div>

  <div v-else class="table-scroll">
    <table class="data-table">
      <thead>
        <tr>
          <th>{{ t('hr.attendanceReport.employee') }}</th>
          <th>{{ t('hr.attendanceReport.code') }}</th>
          <th>{{ t('hr.attendanceReport.totalDays') }}</th>
          <th>{{ t('hr.attendanceReport.attendedDays') }}</th>
          <th>{{ t('hr.attendanceReport.absentDays') }}</th>
          <th>{{ t('hr.attendanceReport.workedHours') }}</th>
          <th>{{ t('hr.attendanceReport.attendanceRate') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.employee_id">
          <td class="cell-name">{{ row.full_name }}</td>
          <td class="cell-code">{{ row.employee_code }}</td>
          <td class="cell-num">{{ row.total_days }}</td>
          <td class="cell-num">{{ row.attended_days }}</td>
          <td class="cell-num">
            <span :class="['status-badge', row.absent_days > 0 ? 'status-critical' : 'status-neutral']">
              {{ row.absent_days }}
            </span>
          </td>
          <td class="cell-num">{{ formatMinutesAsHours(row.total_worked_minutes) }}</td>
          <td class="cell-num">{{ rateOf(row) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
